import { useState, useRef, useEffect } from "react";
import { Bot, X, Send, Sparkles, Loader2, Minus } from "lucide-react";
import { chatWithAI, type AIMessage } from "@/project/lib/ai-engine";

const SUGGESTED = [
  "How does 80G work?",
  "What is In-Kind donation?",
  "Are institutions verified?",
  "Monthly giving setup",
];

export function AIChatbot() {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState<AIMessage[]>([
    {
      role: "assistant",
      content:
        "Namaste! I am Seva AI. Ask me about causes, 80G benefits, or how your donation creates impact.",
      timestamp: Date.now(),
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && !minimized) {
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
        if (!minimized) inputRef.current?.focus();
      }, 50);
    }
  }, [open, minimized, messages]);

  async function send(text?: string) {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setInput("");
    setMinimized(false);
    const userMsg: AIMessage = { role: "user", content, timestamp: Date.now() };
    const next = [...messages, userMsg];
    setMessages(next);
    setLoading(true);
    try {
      const reply = await chatWithAI(next);
      setMessages((m) => [
        ...m,
        { role: "assistant", content: reply, timestamp: Date.now() },
      ]);
    } finally {
      setLoading(false);
    }
  }

  function toggleOpen() {
    if (!open) {
      setOpen(true);
      setMinimized(false);
    } else if (!minimized) {
      setMinimized(true);
    } else {
      setMinimized(false);
    }
  }

  return (
    <>
      {/* FAB button */}
      <button
        id="ai-chatbot-toggle"
        onClick={toggleOpen}
        className="fixed bottom-5 right-5 z-50 flex h-13 w-13 items-center justify-center rounded-full bg-[var(--maroon-deep)] shadow-2xl transition-all hover:scale-105 active:scale-95"
        style={{
          height: 52,
          width: 52,
        }}
        aria-label="Open Seva AI"
      >
        {open && !minimized ? (
          <Bot className="h-5 w-5 text-white" />
        ) : (
          <>
            <Bot className="h-5 w-5 text-white" />
            <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-[var(--saffron)] text-[8px] font-bold text-[var(--maroon-deep)] ring-2 ring-white">
              AI
            </span>
          </>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          className="fixed z-50 flex flex-col rounded-2xl shadow-2xl overflow-hidden transition-all duration-200"
          style={{
            bottom: 68,
            right: 12,
            width: "min(320px, calc(100vw - 24px))",
            height: minimized ? "auto" : 440,
            boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
            border: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          {/* Header — click to minimize/restore */}
          <button
            className="flex w-full shrink-0 items-center gap-2.5 px-4 py-3 text-left bg-[var(--maroon-deep)] text-white"
            onClick={() => setMinimized((v) => !v)}
            aria-label={minimized ? "Expand chat" : "Minimize chat"}
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 shrink-0">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="font-semibold text-white text-sm leading-none">Seva AI</p>
              <p className="text-[10px] text-white/70 mt-0.5">Daan Amrut Foundation</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {minimized ? (
                <span className="text-white/70 text-[10px] px-1">Click to expand</span>
              ) : (
                <Minus className="h-3.5 w-3.5 text-white/80" />
              )}
              <button
                onClick={(e) => { e.stopPropagation(); setOpen(false); setMinimized(false); }}
                className="rounded-full p-1 text-white/70 hover:text-white hover:bg-white/15 transition ml-1"
                aria-label="Close chat"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </button>

          {/* Body — hidden when minimized */}
          {!minimized && (
            <>
              {/* Messages */}
              <div
                className="flex-1 overflow-y-auto p-3 space-y-3"
                style={{ background: "var(--bg)" }}
              >
                {messages.map((m, i) => (
                  <div
                    key={i}
                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    {m.role === "assistant" && (
                      <div className="mr-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--maroon)]/10 mt-1">
                        <Bot className="h-3 w-3 text-[var(--maroon)]" />
                      </div>
                    )}
                    <div
                      className={`max-w-[82%] rounded-2xl px-3 py-2 text-[13px] leading-relaxed ${
                        m.role === "user"
                          ? "bg-[var(--maroon)] text-white rounded-tr-sm"
                          : "bg-white text-[var(--fg)] shadow-sm rounded-tl-sm border border-border/40"
                      }`}
                    >
                      {m.content}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="mr-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--maroon)]/10 mt-1">
                      <Bot className="h-3 w-3 text-[var(--maroon)]" />
                    </div>
                    <div className="flex items-center gap-1.5 rounded-2xl rounded-tl-sm bg-white border border-border/40 px-3 py-2.5 shadow-sm">
                      <Loader2 className="h-3 w-3 animate-spin text-[var(--maroon)]" />
                      <span className="text-[12px] text-foreground/60">Thinking...</span>
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              {/* Suggestions — only on first message */}
              {messages.length <= 1 && (
                <div
                  className="shrink-0 px-3 pb-2 pt-2 flex gap-1.5 flex-wrap border-t border-border/40"
                  style={{ background: "var(--bg)" }}
                >
                  {SUGGESTED.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      className="rounded-full border border-[var(--maroon)]/25 bg-[var(--maroon)]/5 px-2 py-1 text-[11px] text-[var(--maroon)] hover:bg-[var(--maroon)]/10 transition"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}

              {/* Input */}
              <div className="shrink-0 flex items-center gap-2 border-t border-border/40 bg-white p-2.5">
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask about causes, 80G..."
                  className="flex-1 rounded-xl border border-border/50 bg-[var(--bg)] px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[var(--maroon)]/30 placeholder:text-foreground/40"
                />
                <button
                  onClick={() => send()}
                  disabled={!input.trim() || loading}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[var(--maroon)] text-white disabled:opacity-40 hover:bg-[var(--maroon-deep)] transition"
                >
                  <Send className="h-3.5 w-3.5" />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
